import { useEffect, useState, useCallback, useRef, useMemo } from 'react'
import { GlobalState } from '@leverege/ui-redux'
import { DateTime } from 'luxon'
import PlyLayerTriggerHandler from '../queryTriggers/PlyLayerTriggerHandler'
import useNextSAData from './useNextSAData'

const filter = {
  conditions : [
    {
      field : 'data/progress/status',
      not : true,
      type : 'equals',
      value : 'complete'
    },
    {
      field : 'data/progress/status',
      not : true,
      type : 'equals',
      value : 'skipped'
    }
  ],
  operator : 'and',
  type : 'logical'
}

const sort = [
  {
    field : 'data/order/laserLayerExpectedStep',
    missing : '_first',
    order : 'asc'
  },
  {
    field : 'data/progress/startedAt',
    order : 'desc'
  }
]

export default function useData( { delegate } ) {
  const [ plyData, setPlyData ] = useState( [] )
  const [ lastUpdated, setLastUpdated ] = useState( DateTime.now() )
  const [ activeDelegate, setActiveDelegate ] = useState( null )
  const [ isLoading, setIsLoading ] = useState( true )
  
  const isNextSARef = useRef( false )

  const nextSADelegateRef = useRef( null )
  
  const currentSADelegate = useMemo( () => {
    return delegate.child( 'subAssemblyJob' ).child( 'plyLayers' )
  }, [ delegate ] )

  useEffect( () => {
    if ( !activeDelegate ) {
      setActiveDelegate( currentSADelegate )
    }
  }, [ currentSADelegate, activeDelegate ] )
  
  const handleNextSADataChange = useCallback( ( hasData, data ) => {
    if ( hasData && data.length > 0 ) {
      isNextSARef.current = true
      setPlyData( data )
      setLastUpdated( DateTime.now() )
      setActiveDelegate( delegate )
    } else if ( isNextSARef.current ) {
      isNextSARef.current = false
      setActiveDelegate( currentSADelegate )
      // Don't set plyData here - let fetchCurrentSAData handle it
    }
  }, [] )
  
  const { nextSAData, hasNextSA, nextSADelegate } = useNextSAData( {
    delegate,
    onDataChange : handleNextSADataChange
  } )
  
  useEffect( () => {
    nextSADelegateRef.current = nextSADelegate
  }, [ nextSADelegate ] )
  
  const fetchCurrentSAData = useCallback( async () => {
    if ( isNextSARef.current ) return
    
    setIsLoading( true )
    
    try {
      const results = await GlobalState.dispatch( currentSADelegate.search( { filter, sort } ) )
      
      const items = results?.items || []
      
      if ( !isNextSARef.current ) {
        if ( items.length > 0 ) {
          const processedData = items.map( ( item ) => {
            const layerId = item.id
            const detail = currentSADelegate.itemRef( layerId )
            return {
              data : item,
              id : detail.id,
              ref : detail.ref,
              type : detail.type,
            }
          } )
          
          setPlyData( processedData )
        } else {
          setPlyData( [] )
        }
        
        setLastUpdated( DateTime.now() )
      }
    } catch ( err ) {
      console.error( 'Error fetching current SA data:', err )
      
      if ( !isNextSARef.current ) {
        setPlyData( [] )
        setLastUpdated( DateTime.now() )
      }
    } finally {
      setIsLoading( false )
    }
  }, [ currentSADelegate ] )
  
  const context = useMemo( () => {
    return {
      clientProps : {
        delegate : currentSADelegate
      }
    }
  }, [ currentSADelegate ] )
  
  const currentTrigger = useMemo( () => {
    if ( !currentSADelegate ) return null
    
    const t = new PlyLayerTriggerHandler( {
      delegate : currentSADelegate,
      shellDelegate : delegate,
      hasNextSA,
      path : currentSADelegate?.relationship?.path,
      client : 'ItemScreen',
      loadData : fetchCurrentSAData
    }, context )
    
    return t
  }, [ fetchCurrentSAData, context, currentSADelegate, delegate, hasNextSA ] )
  
  useEffect( () => {
    if ( currentTrigger ) {
      currentTrigger.init()
      
      return () => {
        currentTrigger.cleanup()
      }
    }

    return undefined
  }, [ currentTrigger ] )
  
  useEffect( () => {
    if ( !isNextSARef.current ) {
      fetchCurrentSAData()
    }
  }, [ fetchCurrentSAData ] )
  
  useEffect( () => {
    if ( isNextSARef.current && ( !hasNextSA || !nextSAData || nextSAData.length === 0 ) ) {
      isNextSARef.current = false
      setActiveDelegate( currentSADelegate )
      fetchCurrentSAData()
    } else if ( !isNextSARef.current && hasNextSA && nextSAData && nextSAData.length > 0 ) {
      isNextSARef.current = true
      setActiveDelegate( nextSADelegate )
      setPlyData( nextSAData )
      setLastUpdated( DateTime.now() )
    }
  }, [ hasNextSA, nextSAData, fetchCurrentSAData, currentSADelegate, nextSADelegate ] )
  
  return {
    plyData,
    lastUpdated,
    plyLayerDelegate : activeDelegate,
    isNextSA : isNextSARef.current,
    isLoading
  }
}
